/**
 * LVD free delivery progress bar.
 *
 * The theme writes to the cart from a dozen different places (minicart qty,
 * upsells, quick add, wishlist bulk add, shoppable video, apps...) and only
 * some of them bother to notify the UI afterwards. Rather than trusting those
 * call sites, this watches the network itself: every cart write, whoever makes
 * it, ends up broadcasting a single event that the bar renders from.
 */
(function () {
  'use strict';

  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/i;

  function cartUrl() {
    var base = (window.routes && window.routes.cart_url) || '/cart';
    return base.replace(/\/$/, '') + '.js';
  }

  /* ------------------------------------------------------------------ *
   * Cart bus — one shared instance, whoever loads first wins.
   * ------------------------------------------------------------------ */
  var Bus = window.LvdCartLive;

  if (!Bus) {
    Bus = window.LvdCartLive = {
      cart: null,
      _subs: [],
      _inflight: null,
      _timer: null,
      _lastPublish: 0,
      _domWatched: false,

      /** Returns an unsubscribe fn — bars get re-injected by section renders. */
      subscribe: function (fn) {
        var subs = this._subs;
        subs.push(fn);
        if (this.cart) fn(this.cart);
        return function () {
          var i = subs.indexOf(fn);
          if (i > -1) subs.splice(i, 1);
        };
      },

      publish: function (cart) {
        if (!cart || typeof cart.items_subtotal_price === 'undefined') return;
        this.cart = cart;
        this._lastPublish = Date.now();
        this._subs.slice().forEach(function (fn) {
          try {
            fn(cart);
          } catch (e) {
            console.error('[lvd-shipping-bar]', e);
          }
        });
        document.dispatchEvent(new CustomEvent('lvd:cart:update', { detail: cart }));
      },

      /** Coalesces bursts of writes into a single /cart.js read. */
      refresh: function (delay, skipIfFresherThan) {
        var self = this;
        clearTimeout(this._timer);
        this._timer = setTimeout(function () {
          // The write hooks usually publish straight from the response, so a
          // follow-up read from a safety net would just be a wasted request.
          if (skipIfFresherThan && Date.now() - self._lastPublish < skipIfFresherThan) return;
          self._read();
        }, typeof delay === 'number' ? delay : 60);
      },

      _read: function () {
        var self = this;
        if (this._inflight) return this._inflight;
        this._inflight = fetch(cartUrl(), {
          method: 'GET',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        })
          .then(function (res) {
            return res.json();
          })
          .then(function (cart) {
            self.publish(cart);
            return cart;
          })
          .catch(function () {
            /* offline / aborted — the next write retries */
          })
          .then(function (cart) {
            self._inflight = null;
            return cart;
          });
        return this._inflight;
      },

      /**
       * change/update/clear already answer with the whole cart, so we can skip
       * the extra round trip and paint immediately. add.js answers with the
       * line item only, so that one still needs a read.
       */
      _consume: function (url, body) {
        if (/\/cart\/add/i.test(url) || !body) return this.refresh();
        try {
          var cart = JSON.parse(body);
          if (cart && typeof cart.items_subtotal_price !== 'undefined') {
            clearTimeout(this._timer);
            this.publish(cart);
            return;
          }
        } catch (e) {
          /* not JSON (sections-only response) — fall through */
        }
        this.refresh();
      },

      _watch: function () {
        var self = this;

        // 1. fetch()
        var nativeFetch = window.fetch;
        if (typeof nativeFetch === 'function' && !nativeFetch.__lvdPatched) {
          var patched = function (input, init) {
            var url = typeof input === 'string' ? input : (input && input.url) || '';
            var call = nativeFetch.apply(this, arguments);
            if (!CART_WRITE.test(url)) return call;
            return call.then(function (response) {
              response
                .clone()
                .text()
                .then(function (text) {
                  self._consume(url, text);
                })
                .catch(function () {
                  self.refresh();
                });
              return response;
            });
          };
          patched.__lvdPatched = true;
          window.fetch = patched;
        }

        // 2. XMLHttpRequest — some apps and older theme code still use it.
        var proto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
        if (proto && !proto.open.__lvdPatched) {
          var nativeOpen = proto.open;
          var nativeSend = proto.send;
          var open = function (method, url) {
            this.__lvdUrl = url;
            return nativeOpen.apply(this, arguments);
          };
          open.__lvdPatched = true;
          proto.open = open;
          proto.send = function () {
            var xhr = this;
            if (CART_WRITE.test(xhr.__lvdUrl || '')) {
              xhr.addEventListener('loadend', function () {
                self._consume(xhr.__lvdUrl, typeof xhr.responseText === 'string' ? xhr.responseText : null);
              });
            }
            return nativeSend.apply(this, arguments);
          };
        }

        if (this._domWatched || !document.body) return;
        this._domWatched = true;

        // 3. Safety net: anything that changes the cart count without touching
        //    fetch/XHR (server-rendered swaps, app bundles with their own
        //    transport) still moves this number.
        var counter = document.querySelector('.cart-count');
        if (counter && window.MutationObserver) {
          new MutationObserver(function () {
            self.refresh(150, 1000);
          }).observe(counter, { childList: true, characterData: true, subtree: true });
        }

        // 4. Drawer opening, and returning to a bfcached or backgrounded page.
        var drawer = document.getElementById('bls-header_minicart');
        if (drawer && window.MutationObserver) {
          new MutationObserver(function () {
            if (drawer.classList.contains('bls__opend-popup-header')) self.refresh(0, 1000);
          }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
        }
        window.addEventListener('pageshow', function (e) {
          if (e.persisted) self.refresh(0);
        });
        document.addEventListener('visibilitychange', function () {
          if (!document.hidden) self.refresh(400, 2000);
        });
      }
    };

    if (document.readyState === 'loading') {
      // Patch the transports right away; DOM-bound observers once we have a DOM.
      Bus._watch();
      document.addEventListener('DOMContentLoaded', function () {
        Bus._watch();
      });
    } else {
      Bus._watch();
    }
  }

  /* ------------------------------------------------------------------ *
   * Money formatting (Shopify's money_format placeholders).
   * ------------------------------------------------------------------ */
  function formatWithDelimiters(number, precision, thousands, decimal) {
    if (isNaN(number) || number === null) return '0';
    var fixed = (number / 100).toFixed(precision);
    var parts = fixed.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }

  function formatMoney(cents, format) {
    var value = '';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || '{{amount}}';

    switch ((fmt.match(placeholder) || [])[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
    }
    return fmt.replace(placeholder, value);
  }

  /* ------------------------------------------------------------------ *
   * The element.
   * ------------------------------------------------------------------ */
  class LvdShippingBar extends HTMLElement {
    connectedCallback() {
      this.threshold = parseInt(this.dataset.threshold, 10) || 0;
      this.moneyFormat =
        this.dataset.moneyFormat || (window.cartStrings && window.cartStrings.money_format) || '{{amount}}';
      this.fill = this.querySelector('[data-fsb-fill]');
      this.track = this.querySelector('[data-fsb-track]');
      this.message = this.querySelector('[data-fsb-message]');
      this.unlocked = this.classList.contains('is-unlocked');

      // Paint from whatever the bus already knows, so a bar that was just
      // injected is never a frame behind. Only fall back to the server-rendered
      // subtotal when nothing has read the cart yet.
      if (this._unsubscribe) this._unsubscribe();
      this._unsubscribe = Bus.subscribe((cart) => this.render(cart.items_subtotal_price));
      if (!Bus.cart) {
        this.render(parseInt(this.dataset.subtotal, 10) || 0);
        // Cached/CDN-served HTML can carry a stale subtotal, so confirm once.
        Bus.refresh(150);
      }
    }

    disconnectedCallback() {
      if (this._unsubscribe) {
        this._unsubscribe();
        this._unsubscribe = null;
      }
    }

    render(subtotal) {
      if (!this.threshold) return;

      const total = Math.max(0, Number(subtotal) || 0);
      const remaining = Math.max(0, this.threshold - total);
      const percent = Math.min(100, Math.round((total / this.threshold) * 1000) / 10);
      const isFree = remaining <= 0;

      if (this.fill) this.fill.style.width = percent + '%';
      if (this.track) {
        this.track.setAttribute('aria-valuenow', String(Math.round(percent)));
        this.track.setAttribute(
          'aria-valuetext',
          isFree ? 'Free delivery unlocked' : `${formatMoney(remaining, this.moneyFormat)} to free delivery`
        );
      }

      this.classList.toggle('is-empty', total === 0);
      this.classList.toggle('is-unlocked', isFree);

      // Celebrate the crossing, not every repaint.
      if (isFree && !this.unlocked) {
        this.classList.remove('is-celebrating');
        void this.offsetWidth;
        this.classList.add('is-celebrating');
      }
      this.unlocked = isFree;

      if (this.message) {
        this.message.innerHTML = isFree
          ? `<strong>${this.dataset.msgSuccess || 'Free delivery unlocked!'}</strong>`
          : (this.dataset.msgRemaining || "You're {amount} away from free delivery").replace(
              '{amount}',
              `<strong class="lvd-fsb__amount">${formatMoney(remaining, this.moneyFormat)}</strong>`
            );
      }
    }
  }

  if (!customElements.get('lvd-shipping-bar')) {
    customElements.define('lvd-shipping-bar', LvdShippingBar);
  }
})();
